"use client";

import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Extend Window interface to include bootstrap
declare global {
  interface Window {
    bootstrap: {
      Collapse: new (element: HTMLElement, options?: { toggle?: boolean }) => {
        hide: () => void;
        show: () => void;
        toggle: () => void;
      };
    };
  }
}

const TopMenu = () => {
  const { t } = useTranslation();
  const collapseMenuRef = useRef<HTMLDivElement | null>(null);
  const collapseButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleNavLinkClick = () => {
    const collapsibleMenu = collapseMenuRef.current;
    const collapsibleButton = collapseButtonRef.current;

    if (collapsibleMenu && collapsibleMenu.classList.contains("show")) {
      // Try to use Bootstrap's collapse method first
      try {
        if (window.bootstrap) {
          const bsCollapse = new window.bootstrap.Collapse(collapsibleMenu, {
            toggle: false,
          });
          bsCollapse.hide();
        } else {
          // Fallback: manually remove the show class and update button state
          collapsibleMenu.classList.remove("show");
          collapsibleButton?.classList.add("collapsed");
          collapsibleButton?.setAttribute("aria-expanded", "false");
        }
      } catch {
        // If Bootstrap method fails, use manual approach
        collapsibleMenu.classList.remove("show");
        collapsibleButton?.classList.add("collapsed");
        collapsibleButton?.setAttribute("aria-expanded", "false");
      }
    }
  };

  return (
    <div className="col-auto menu-order position-static">
      <button
        className="navbar-toggler float-start"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarNav"
        aria-controls="navbarNav"
        aria-label="Toggle navigation"
        ref={collapseButtonRef}
      >
        <span className="navbar-toggler-line" />
        <span className="navbar-toggler-line" />
        <span className="navbar-toggler-line" />
        <span className="navbar-toggler-line" />
      </button>
      <div
        className="collapse navbar-collapse"
        id="navbarNav"
        ref={collapseMenuRef}
      >
        <ul className="navbar-nav alt-font ls-05px">
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.home")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/">
                  {t("navigation.home")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/overview/">
                  {t("navigation.overview")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/welcome-message/">
                  {t("navigation.welcomeMessage")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/organizing-committee/">
                  {t("navigation.organization")}
                </Link>
              </li>
            </ul>
          </li>
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.conference")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/conference-dates/">
                  {t("navigation.conferenceDates")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/schedule/">
                  {t("navigation.schedule")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/speakers/">
                  {t("navigation.speakers")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/symposiums/">
                  {t("navigation.symposiums")}
                </Link>
              </li>
              <li>
                <Link
                  onClick={handleNavLinkClick}
                  to="/pre-conference-workshops/"
                >
                  {t("navigation.preConferenceWorkshops")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/registration/">
                  {t("navigation.conferenceRegistration")}
                </Link>
              </li>
              {/* Submit Your Paper link temporarily hidden */}
               <li>
                <a
                  href="https://www.openconf.org/apcg2026_saudiarabia/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("navigation.submitYourPaper")}
                </a>
              </li> 
              <li>
                <Link onClick={handleNavLinkClick} to="/field-activities/">
                  {t("navigation.fieldActivities")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/conference-booklet/">
                  {t("navigation.resources")}
                </Link>
              </li>
            </ul>
          </li>
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.conferenceOutput")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <a
                  href="/pdfs/APCG_2026_Report.pdf"
                  download
                  onClick={handleNavLinkClick}
                >
                  {t("navigation.apcgReport")}
                </a>
              </li>
              <li>
                <a
                  href="https://netorg19272376-my.sharepoint.com/personal/info_apcg2026-saudiarabia_org/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Finfo%5Fapcg2026%2Dsaudiarabia%5Forg%2FDocuments%2FAPCG2026%20Photos&ga=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleNavLinkClick}
                >
                  {t("navigation.apcgPhotos")}
                </a>
              </li>
            </ul>
          </li>
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.youthSummit")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/youth-summit/">
                  {t("navigation.overview")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/youth-summit-schedule/">
                  {t("navigation.schedule")}
                </Link>
              </li>
              <li>
                <Link
                  onClick={handleNavLinkClick}
                  to="/youth-summit-registration/"
                >
                  {t("navigation.youthSummitRegistration")}
                </Link>
              </li>
              {/*<li>
                <Link onClick={handleNavLinkClick} to="/youth-summit-payment/">
                  {t("navigation.youthSummitPayment")}
                </Link>
              </li>*/}
            </ul>
          </li>
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.gifthone")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/gifthone/">
                  {t("navigation.gifthoneOverview")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/call-for-mentors/">
                  {t("navigation.callForMentors")}
                </Link>
              </li>
            </ul>
          </li>
          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.callForPapers")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/important-dates/">
                  {t("navigation.importantDates")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/submission-guidelines/">
                  {t("navigation.submissionGuidelines")}
                </Link>
              </li>
            
              {/* Submit Your Paper link temporarily hidden */}
              <li>
                <a
                  href="https://www.openconf.org/apcg2026_saudiarabia/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("navigation.submitYourPaper")}
                </a>
              </li> 
              <li>
                <Link onClick={handleNavLinkClick} to="/resources/">
                  {t("navigation.resources")}
                </Link>
              </li>
            </ul>
          </li>

          <li className="nav-item dropdown dropdown-with-icon-style02">
            <a href="#" className="nav-link">
              {t("navigation.venue")}
            </a>
            <i
              className="fa-solid fa-angle-down dropdown-toggle"
              role="button"
              data-bs-toggle="dropdown"
            ></i>
            <ul className="dropdown-menu">
              <li>
                <Link onClick={handleNavLinkClick} to="/about-ubt/">
                  {t("navigation.aboutUBT")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/accomodation/">
                  {t("navigation.accommodation")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/jeddah-overview/">
                  {t("navigation.jeddahOverview")}
                </Link>
              </li>
              <li>
                <Link onClick={handleNavLinkClick} to="/visa-information/">
                  {t("navigation.visaInformation")}
                </Link>
              </li>
            </ul>
          </li>
          <li className="nav-item">
            <Link onClick={handleNavLinkClick} to="/FAQs/" className="nav-link">
              {t("navigation.faqs")}
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TopMenu;
